// ADR 25.9.26g §4 — the install offered at the door.
//
// Wherever there is no native app a visitor can install, the web offers to
// install itself, signed in or not. The account menu's "Install app" reaches
// signed-in learners only (the header renders signed-in, 8.9.26i §3); this
// strip reads **no session** and shows on `/signin`, `/vocab` and `/plans`
// alike. It retired 17.9.26k's Android beta strip and took its place.
//
// A NULL-CAPABLE SLOT OF THE SHELL'S OUTER COLUMN, in 17.9.26k's structure:
// always a child, `null` when it has nothing to show, so the frame's children
// never renumber and the `<Outlet>` is reconciled, not remounted
// (`AdaptiveShell.tsx`'s invariant). That matters twice here: the strip can
// appear mid-session (`beforeinstallprompt` fires late) and disappear on a
// press (`promptInstall()` spends the prompt, the affordance becomes `none`).
//
// Since 25.9.26h the strip also opens on the five browser platforms whose own
// menu installs (`menu_instructions`), before any prompt fires. When a prompt
// is captured mid-session the CTA becomes the prompt and open steps close:
// they are remembered against the affordance they were opened for.
//
// Since 6.10.26a the copy says computer or Home Screen (`deviceOf`, §2), and an
// `?install=1` link mounts the strip `requested` (§5): past the dismissal, its
// steps open (or its button focused — a page load is no gesture, so never the
// prompt itself), the link counted once and the parameter stripped.
//
// Since 6.10.26b §3 a browser that cannot install but can hand on (`handoff`)
// opens the handoff panel where the steps would be, titled by its target's
// device, with the boot-latched `chromeMissing()`.
//
// Must not import the wasm facade: it is in 25.9.26f's boot graph.

import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'

import { useInviteDismissed } from '../layout/useInviteDismissed'
import {
  emitInstallLinkOpened,
  emitInstallStripClicked,
  emitInstallStripDismissed,
  emitInstallStripShown,
  type StripAffordance,
  type StripContext,
} from '../telemetry/installStrip'
import { InstallBoundary } from '../telemetry/field/InstallBoundary'
import { Button } from '../ui/Button'
import { IconButton } from '../ui/controls'
import { deviceOfTarget } from './handoff'
import { deviceOf, type Device } from './installAffordance'
import { withoutInstall } from './installDeepLink'
import { InstallPanel, type PanelState } from './InstallHandoff'
import { closeInstallRequest, openInstallRequest, promptInstall } from './installPrompt'
import { useInstallAffordance, type InstallState } from './useInstallAffordance'

/** Every affordance the strip draws: all but `none`. */
type StripState = Exclude<InstallState, { affordance: 'none' }>

export const INSTALL_STRIP_DISMISSED_KEY = 'lingara.pwa_install.dismissed'

/** 6.10.26a §2 — `unknown` keeps 25.9.26g's generic pair. */
const COPY: Record<Device, { title: string; body: string }> = {
  computer: { title: 'pwa_install_strip_title_computer', body: 'pwa_install_strip_body_computer' },
  handheld: { title: 'pwa_install_strip_title_handheld', body: 'pwa_install_strip_body_handheld' },
  unknown: { title: 'pwa_install_strip_title', body: 'pwa_install_strip_body' },
}

/**
 * 6.10.26a §5 — on mount, act on a held `?install=1` request exactly once.
 * Opened in an effect, never in render: StrictMode double-invokes renders,
 * and `openInstallRequest` answers `true` only on `pending → open`, so the
 * effect's second run under StrictMode, and any later remount, does nothing.
 */
function useOpenOnRequest(requested: boolean, strip: StripContext, onOpen: () => void): void {
  const { search, hash } = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if (!requested || !openInstallRequest()) return
    onOpen()
    emitInstallLinkOpened(strip)
    navigate({ search: withoutInstall(search), hash }, { replace: true })
    // Mount only: the request is read once, and the URL it strips is the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

interface InstallStripProps {
  state: StripState
  onDismiss: () => void
  requested?: boolean
}

/**
 * The strip itself: a title, a line, the CTA and the dismiss.
 *
 * The row wraps and the steps take a full row beneath it (`basis-full`), so
 * the steps' `min-w-64` card never squeezes the title on a phone. The steps'
 * "OK" closes them and does not dismiss the strip.
 *
 * `shown` lives **here, never in the slot**: the slot mounts on every route
 * and returns `null` on most of them. Once per tab, latched on delivery — the
 * emitter owns both halves.
 */
export function InstallStrip({ state, onDismiss, requested = false }: InstallStripProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const buttonId = useId()
  const { pathname } = useLocation()
  const [stepsOpenFor, setStepsOpenFor] = useState<StripAffordance | null>(null)
  const strip: StripContext = { affordance: state.affordance, platform: state.platform, iosBrowser: state.iosBrowser }

  useEffect(() => {
    emitInstallStripShown(strip, pathname)
    // Once per tab, not per route: the latch is the emitter's, so the first
    // pathname is the one that reaches the wire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useOpenOnRequest(requested, strip, () => {
    if (state.affordance === 'prompt') document.getElementById(buttonId)?.focus()
    else setStepsOpenFor(state.affordance)
  })

  const onInstall = () => {
    emitInstallStripClicked(strip)
    if (state.affordance === 'prompt') void promptInstall('strip')
    else setStepsOpenFor((open) => (open === state.affordance ? null : state.affordance))
  }
  const onClose = () => {
    emitInstallStripDismissed(strip)
    closeInstallRequest()
    onDismiss()
  }

  return (
    <aside
      aria-labelledby={titleId}
      data-testid="install-strip"
      className="flex flex-wrap items-center gap-3 border-b border-boundary bg-surface-sunken px-4 py-2"
    >
      <StripCopy titleId={titleId} device={stripDevice(state)} />
      <Button id={buttonId} variant="primary" onClick={onInstall}>{t('pwa_install_app')}</Button>
      <IconButton icon="close" label={t('limit_dismiss')} onClick={onClose} />
      {state.affordance !== 'prompt' && stepsOpenFor === state.affordance && (
        <StripPanel state={state} onClose={() => setStepsOpenFor(null)} />
      )}
    </aside>
  )
}

/** 6.10.26b §3 — every handoff is `platform: 'other'`, so its device comes from its target. */
function stripDevice(state: StripState): Device {
  return state.affordance === 'handoff' ? deviceOfTarget(state.target) : deviceOf(state.platform)
}

/** The title and the line, worded for the device class (6.10.26a §2). */
function StripCopy({ titleId, device }: { titleId: string; device: Device }) {
  const { t } = useTranslation()
  const copy = COPY[device]
  return (
    <div className="min-w-0 flex-1 text-sm">
      <p id={titleId} className="font-medium">
        {t(copy.title)}
      </p>
      <p className="text-ink-muted">{t(copy.body)}</p>
    </div>
  )
}

/** The platform's steps, or the handoff panel, on a full row of their own beneath the strip's. */
function StripPanel({ state, onClose }: { state: PanelState; onClose: () => void }) {
  return (
    <div className="basis-full">
      <InstallPanel state={state} onClose={onClose} />
    </div>
  )
}

/**
 * ADR 25.9.26g §4 — the slot, and the order it decides in.
 *
 * Every hook is called first, unconditionally, above every early return. Then,
 * each a `null`: a shared plan carries no app chrome (8.9.26i AC4 — the same
 * `useMatch` `HeaderSlot` uses); no install path (`none`, which also covers
 * standalone and in-app browsers); already dismissed. A dismissed strip does
 * not hide the account-menu entry. 6.10.26a §5 — `requested` (an `?install=1`
 * link) goes past the dismissal: the visitor asked for exactly this.
 */
export function InstallStripSlot({ requested = false }: { requested?: boolean }) {
  return (
    <InstallBoundary surface="strip">
      <InstallStripSlotBody requested={requested} />
    </InstallBoundary>
  )
}

/** ADR 15.10.26d §2 — the slot's body, behind the boundary above. */
function InstallStripSlotBody({ requested }: { requested: boolean }) {
  const onShared = useMatch('/s/:token') !== null
  const state = useInstallAffordance()
  const [dismissed, dismiss] = useInviteDismissed(INSTALL_STRIP_DISMISSED_KEY)
  if (onShared) return null
  if (state.affordance === 'none') return null
  if (dismissed && !requested) return null
  return <InstallStrip state={state} onDismiss={dismiss} requested={requested} />
}
